import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal, Select } from 'antd';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { pickDefaultLanguage, useActiveLanguages } from '@features/languages/useLanguages';
import {
  WIDGET_PLACEMENTS,
  type SliderCreateInput,
  type SliderListItem,
  type SliderUpdateInput,
} from '../types';

interface FormValues {
  name: string;
  placement: string;
  languageId?: string;
}

interface SliderFormModalProps {
  open: boolean;
  editing: SliderListItem | null;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: SliderCreateInput) => void;
  onUpdate: (id: string, input: SliderUpdateInput) => void;
}

/** Slider meta formu (ad/yerleşim/dil). Görseller ayrı çekmecede yönetilir. */
export function SliderFormModal({
  open,
  editing,
  saving,
  onCancel,
  onCreate,
  onUpdate,
}: SliderFormModalProps) {
  const { t } = useTranslation(['widgets', 'common']);
  const td = t as unknown as (key: string) => string;
  const [form] = Form.useForm<FormValues>();
  const isEdit = editing !== null;
  const { data: languages = [] } = useActiveLanguages();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: editing?.name ?? '',
      placement: editing?.placement ?? WIDGET_PLACEMENTS[0],
      languageId: editing ? undefined : pickDefaultLanguage(languages)?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, languages]);

  const onOk = () => {
    void (async () => {
      try {
        const v = await form.validateFields();
        if (isEdit && editing) onUpdate(editing.id, { name: v.name.trim(), placement: v.placement });
        else onCreate({ name: v.name.trim(), placement: v.placement, languageId: v.languageId ?? null });
      } catch {
        // inline validation
      }
    })();
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t('slider.editTitle') : t('slider.createTitle')}
      onCancel={onCancel}
      onOk={onOk}
      okText={isEdit ? t('common:actions.save') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item name="name" label={t('fields.name')} rules={[{ required: true, message: t('fields.nameRequired') }]}>
          <Input maxLength={120} placeholder={t('slider.namePlaceholder')} />
        </Form.Item>
        <Form.Item name="placement" label={t('fields.placement')} rules={[{ required: true }]}>
          <Select options={WIDGET_PLACEMENTS.map((p) => ({ value: p, label: td(`placement.${p}`) }))} />
        </Form.Item>
        {!isEdit ? (
          <Form.Item label={t('common:contentLanguage.label')} help={t('fields.languageHint')}>
            <Form.Item name="languageId" noStyle>
              <LanguagePickerField />
            </Form.Item>
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
}

function LanguagePickerField({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (v: string | undefined) => void;
}) {
  return <LanguagePicker value={value} onChange={(v) => onChange?.(v)} style={{ width: '100%' }} />;
}
